import { systemPageSlugs } from './system-pages'

/*
 * Menu d'administration personnalisé (#332).
 *
 * L'arborescence par défaut vit dans le code (`app/utils/adminNavigation.ts`).
 * Les super-administrateurs du SSO peuvent la réordonner, y ajouter les pages
 * système et en retirer les entrées de pages. Seul l'écart est enregistré :
 * une entrée ajoutée plus tard au code apparaît d'office.
 */

/**
 * Configuration enregistrée.
 *
 * - `groupes` : pour chaque groupe (clé de `NavGroup`), l'ordre de ses entrées
 *   de premier niveau, par identifiant. Une entrée peut changer de groupe.
 * - `retirees` : entrées de pages retirées du menu. Les écrans fixes (Articles,
 *   Médiathèque…) n'y figurent jamais : on les déplace, on ne les retire pas.
 *
 * L'identifiant d'une page système sans entrée dans le code est `page:<slug>`.
 */
export interface MenuAdministrationConfig {
  groupes: Record<string, string[]>
  retirees: string[]
  /**
   * Entrées de chaque sous-menu, par identifiant de l'entrée qui le porte
   * (#339). Facultatif : absent des configurations enregistrées avant lui.
   */
  sousMenus?: Record<string, string[]>
  /**
   * Libellés renommés, par identifiant d'entrée (sous-menus compris) ou de
   * section (`section:<clé>`). Une entrée absente garde son libellé d'origine.
   */
  libelles?: Record<string, string>
  /** Icônes changées, par identifiant d'entrée (#343). */
  icones?: Record<string, string>
  /** Ordre des sections, par clé ; une section non citée suit, à sa place d'origine (#343). */
  sections?: string[]
  /** Sections créées depuis l'écran (#343). */
  sectionsCreees?: SectionCreee[]
  /** Liens libres et dossiers créés depuis l'écran (#343). */
  elements?: ElementCree[]
}

/** Section créée à la main : visible de tous dès qu'elle contient une entrée visible. */
export interface SectionCreee {
  cle: string
  label: string
}

/**
 * Lien libre ou dossier créé à la main, visible de tous.
 *
 * - lien : une adresse interne (`/…`) ou externe (`https://…`) ;
 * - dossier : un sous-menu sans lien, qui ne s'affiche que s'il contient une
 *   entrée visible.
 */
export interface ElementCree {
  id: string
  type: 'lien' | 'dossier'
  label: string
  icone: string
  url?: string
  nouvelOnglet?: boolean
}

/** Saisie de l'écran pour une section, un lien ou un dossier. */
export interface ValeursElement {
  label: string
  icone: string
  url: string
  nouvelOnglet: boolean
}

export const MENU_CONFIG_VIDE: MenuAdministrationConfig = {
  groupes: {},
  retirees: [],
  sousMenus: {},
  libelles: {},
  icones: {},
  sections: [],
  sectionsCreees: [],
  elements: [],
}

export const PREFIXE_SECTION = 'section-'
export const PREFIXE_LIEN = 'lien-'
export const PREFIXE_DOSSIER = 'dossier-'
/** Clé de libellé d'une section, distincte des identifiants d'entrée. */
export const cleLibelleSection = (cle: string) => `section:${cle}`

const ICONE = /^i-lucide-[a-z0-9-]{1,60}$/
const URL_MAX = 500

/**
 * Adresse acceptée pour un lien libre : interne (`/…`, sans `//` qui serait
 * une adresse externe déguisée) ou externe en `http(s)`. Tout autre schéma —
 * `javascript:` en tête — est refusé.
 */
export function urlValide(url: string): boolean {
  if (url.length > URL_MAX || /\s/.test(url)) return false
  if (url.startsWith('/')) return !url.startsWith('//')
  return /^https?:\/\/[^/\s]+/i.test(url)
}

export const estUrlExterne = (url: string) => /^https?:\/\//i.test(url)

/** Préfixe des entrées de pages système ajoutées depuis la base. */
export const PREFIXE_PAGE = 'page:'

const ID = /^[a-z0-9:-]{1,120}$/
const MAX_ENTREES = 300
/** Longueur d'un libellé renommé : au-delà, la barre latérale le couperait. */
export const LIBELLE_MAX = 60

/**
 * Lit une configuration reçue ou stockée. Rend `null` si elle est illisible :
 * le serveur la refuse, l'écran retombe sur le menu par défaut.
 */
export function lireConfigurationMenu(brut: unknown): MenuAdministrationConfig | null {
  if (!brut || typeof brut !== 'object') return null
  const { groupes, retirees, sousMenus, libelles, icones, sections, sectionsCreees, elements } = brut as Record<string, unknown>
  if (!groupes || typeof groupes !== 'object' || Array.isArray(groupes)) return null
  if (!Array.isArray(retirees)) return null

  const lus: Record<string, string[]> = {}
  let total = 0
  for (const [cle, ids] of Object.entries(groupes as Record<string, unknown>)) {
    if (!ID.test(cle) || !Array.isArray(ids)) return null
    if (!ids.every(id => typeof id === 'string' && ID.test(id))) return null
    total += ids.length
    lus[cle] = [...new Set(ids as string[])]
  }
  if (!retirees.every(id => typeof id === 'string' && ID.test(id))) return null

  const sous: Record<string, string[]> = {}
  if (sousMenus !== undefined) {
    if (!sousMenus || typeof sousMenus !== 'object' || Array.isArray(sousMenus)) return null
    for (const [cle, ids] of Object.entries(sousMenus as Record<string, unknown>)) {
      if (!ID.test(cle) || !Array.isArray(ids)) return null
      if (!ids.every(id => typeof id === 'string' && ID.test(id))) return null
      total += ids.length
      sous[cle] = [...new Set(ids as string[])]
    }
  }

  const noms: Record<string, string> = {}
  if (libelles !== undefined) {
    if (!libelles || typeof libelles !== 'object' || Array.isArray(libelles)) return null
    for (const [cle, nom] of Object.entries(libelles as Record<string, unknown>)) {
      if (!ID.test(cle) || typeof nom !== 'string') return null
      const propre = nom.trim()
      if (propre.length > LIBELLE_MAX) return null
      if (propre) noms[cle] = propre
    }
    total += Object.keys(noms).length
  }

  const icns: Record<string, string> = {}
  if (icones !== undefined) {
    if (!icones || typeof icones !== 'object' || Array.isArray(icones)) return null
    for (const [cle, icone] of Object.entries(icones as Record<string, unknown>)) {
      if (!ID.test(cle) || typeof icone !== 'string' || !ICONE.test(icone)) return null
      icns[cle] = icone
    }
    total += Object.keys(icns).length
  }

  let ordreSections: string[] = []
  if (sections !== undefined) {
    if (!Array.isArray(sections) || !sections.every(c => typeof c === 'string' && ID.test(c))) return null
    ordreSections = [...new Set(sections as string[])]
    total += ordreSections.length
  }

  const creees: SectionCreee[] = []
  if (sectionsCreees !== undefined) {
    if (!Array.isArray(sectionsCreees)) return null
    for (const brute of sectionsCreees) {
      const { cle, label } = (brute ?? {}) as Record<string, unknown>
      if (typeof cle !== 'string' || !cle.startsWith(PREFIXE_SECTION) || !ID.test(cle)) return null
      if (typeof label !== 'string' || !label.trim() || label.trim().length > LIBELLE_MAX) return null
      if (!creees.some(c => c.cle === cle)) creees.push({ cle, label: label.trim() })
    }
    total += creees.length
  }

  const crees: ElementCree[] = []
  if (elements !== undefined) {
    if (!Array.isArray(elements)) return null
    for (const brut of elements) {
      const { id, type, label, icone, url, nouvelOnglet } = (brut ?? {}) as Record<string, unknown>
      if (type !== 'lien' && type !== 'dossier') return null
      const prefixe = type === 'lien' ? PREFIXE_LIEN : PREFIXE_DOSSIER
      if (typeof id !== 'string' || !id.startsWith(prefixe) || !ID.test(id)) return null
      if (typeof label !== 'string' || !label.trim() || label.trim().length > LIBELLE_MAX) return null
      if (typeof icone !== 'string' || !ICONE.test(icone)) return null
      const element: ElementCree = { id, type, label: label.trim(), icone }
      if (type === 'lien') {
        if (typeof url !== 'string' || !urlValide(url.trim())) return null
        element.url = url.trim()
        element.nouvelOnglet = nouvelOnglet === true
      }
      if (!crees.some(e => e.id === id)) crees.push(element)
    }
    total += crees.length
  }

  if (total + retirees.length > MAX_ENTREES) return null

  return {
    groupes: lus,
    retirees: [...new Set(retirees as string[])],
    sousMenus: sous,
    libelles: noms,
    icones: icns,
    sections: ordreSections,
    sectionsCreees: creees,
    elements: crees,
  }
}

/** Page système à la racine, telle que le menu la connaît. */
export interface PageSystemeMenu {
  slug: string
  title: string
}

/** Pourquoi le changement est refusé, ou `null`. */
export function motifRefusPageSysteme(page: { slug: string, parentId: number | null, status: string }, versSysteme: boolean): string | null {
  // Les pages de cible sont système par construction, et leur cible les gère.
  if (page.parentId !== null) return 'Seule une page à la racine du site peut être une page système.'

  if (!versSysteme && systemPageSlugs.has(page.slug)) {
    return 'Cette page est exigée par le site : elle reste une page système.'
  }

  // Une page système est toujours publiée : la promouvoir en brouillon la
  // figerait dans un état que l'écran refuse ensuite de quitter.
  if (versSysteme && page.status !== 'published') {
    return 'Publiez la page avant d\'en faire une page système.'
  }

  return null
}
